import { CalculateConfigurationTrialParticipantSourceDataAllocations, ConfigurationStatus, ConfigurationTrial as ConfigurationTrialDto, SaveConfigurationTrial, SourceDataAllocationType } from "@/api/dtos";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Text from "@/components/forms/inputs/Text";
import SetLinks from "@/components/layouts/SetLinks";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationTrialInstitutionCreateLink, ConfigurationTrialTrialCreateLink } from "@/contexts/layouts/LinkContext";
import useConfigurationTrial from "@/hooks/data/useConfigurationTrial";
import { FC, useCallback, useContext, useMemo } from "react";

import UneditableText from "@/components/forms/inputs/UneditableText";
import ConfigurationContextTemplate, { IConfigurationContextTemplateProps } from "@/components/forms/templates/ConfigurationContextTemplate";
import { useLocation } from "react-router-dom";


const allocationTypes = [
    SourceDataAllocationType.Constant,
    SourceDataAllocationType.FirstXParticipants,
    SourceDataAllocationType.RepeatingCycle,
    SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle
];

const statuses = [
    ConfigurationStatus.Disabled,
    ConfigurationStatus.ReadOnly,
    ConfigurationStatus.Enabled
];



const ConfigurationTrial: FC = () => {
    const { data: trial, oid: trialOid } = useContext(TrialContext);

    const contextQuery = useMemo(() => {
        if (!trial?.id) {
            return undefined;
        }

        return {
            trialId: trial?.id
        }
    }, [trial]);

    const { data: configurationTrial, loading: configurationTrialLoading, validating: configurationTrialValidating } = useConfigurationTrial(contextQuery, { revalidateOnFocus: false });

    const loading = configurationTrialLoading;
    const validating = configurationTrialValidating;

    const location = useLocation();

    const returnUrl = useMemo(() => {
        if (location.state?.from) {
            return `${location.state.from.pathname}${location.state.from.search}`
        }

        return `/${trialOid}/configuration`;
    }, [trialOid, location])

    const onSave = useCallback<IConfigurationContextTemplateProps<ConfigurationTrialDto>['onSave']>(async (client, data, allocations, updates) => {
        await client.post(new SaveConfigurationTrial({ data: data, allocations: allocations, participantSourceDataAllocationUpdates: updates}));
    }, []);

    const onCalculateAllocations = useCallback<IConfigurationContextTemplateProps<ConfigurationTrialDto>['onCalculateAllocations']>(async (client, contextQuery, data, allocations) => {
        const response = await client.post(new CalculateConfigurationTrialParticipantSourceDataAllocations({ 
            ...contextQuery,
            configurationTrial: data, 
            allocations: allocations
        }));

        return response;
    }, []);
    
    const onValidate = useCallback<IConfigurationContextTemplateProps<ConfigurationTrialDto>['onValidate']>(async (client, errors, formState) => {

        return errors;
    }, []);

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationTrialTrialCreateLink} 
                createInstitutionLink={ConfigurationTrialInstitutionCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <ConfigurationContextTemplate
                allocationTypes={allocationTypes}
                entityName="Trial"
                onCalculateAllocations={onCalculateAllocations}
                onSave={onSave}
                onValidate={onValidate}
                statuses={statuses}
                configuration={configurationTrial}
                configurationLoading={loading}
                configurationValidating={validating}
                contextQuery={contextQuery}
                returnUrl={returnUrl}
                children={
                    <>
                        <InputOnlyField
                            propertyPath="originalName"
                            label="Default Name"
                            helperText="The Default Name of the trial."
                            margin="normal"
                            component={UneditableText}
                            disabled
                            value={trial?.defaultName}
                        />
                        <InputOnlyField
                            propertyPath="originalName"
                            label="Default Protocol"
                            helperText="The Default Protocol of the trial."
                            margin="normal"
                            component={UneditableText}
                            disabled
                            value={trial?.defaultProtocol}
                        />
                        <InputOnlyField
                            propertyPath="configuration.name"
                            label="Name"
                            helperText="Enter a name to override the Default Name of the trial."
                            margin="normal"
                            component={Text}
                        />
                        <InputOnlyField
                            propertyPath="configuration.protocol"
                            label="Protocol"
                            helperText="Enter a protocol to override the Default Protocol of the trial."
                            margin="normal"
                            component={Text}
                        />
                    </>
                }
            />
        </>
    )
};

export default ConfigurationTrial;